import { createSelector } from '@reduxjs/toolkit'
import { getCarHireItems } from 'checkout/selectors/view/carHire'
import { getCarHireTotals } from 'checkout/selectors/payment/carHire'
import { selectBusinessTravellerApprovalPolicyForUser } from './selectBusinessTravellerApprovalPolicyForUser'
import { selectBusinessTravellerCurrentApprovalRequest } from './selectBusinessTravellerCurrentApprovalRequest'
import { selectIsLebtItemPoliciesEnabled } from './itemPolicy/selectIsLebtItemPoliciesEnabled'
import { selectSelectedTravellerEmployeeIds } from './itemPolicy/selectSelectedTravellerEmployeeIds'
import { generateItemPolicyCarHireBudgetKey } from 'businessTraveller/utils/generateItemPolicyKeys'

const LEGACY_CAR_HIRE_BUDGET_KEY = 'car_hire'

export interface CarHireBudgetInfo {
  approvedBudget: number
  carHireBudgetTotal: number
  carHireTotal: number
  hasMissingBudget: boolean
  isOutOfBudget: boolean
  showBudgetBreakdown: boolean
}

function getCarHireItemPolicyBudgetKey(params: {
  carHireItems: Array<App.Checkout.CarHireItem>
  currencyCode: string
  employeeIds: Array<string>
}): string | undefined {
  const carHireItem = params.carHireItems[0]
  if (!carHireItem) {
    return undefined
  }

  const employeeIds = params.employeeIds.slice(0, 1)
  return generateItemPolicyCarHireBudgetKey({
    currencyCode: params.currencyCode,
    numberOfDays: carHireItem.offer.duration,
    travellerCount: Math.max(1, employeeIds.length),
    employeeIds,
  })
}

export const selectBusinessTravellerCarHireBudgetStatus = createSelector(
  selectIsLebtItemPoliciesEnabled,
  (state: App.State) => state.businessTraveller.businessCarHireBudgets,
  (state: App.State) => state.businessTraveller.itemPolicyEvaluations ?? {},
  getCarHireItems,
  (state: App.State) => state.geo.currentCurrency,
  selectSelectedTravellerEmployeeIds,
  (
    isItemPoliciesEnabled,
    businessCarHireBudgets,
    itemPolicyEvaluations,
    carHireItems,
    currencyCode,
    employeeIds,
  ): App.BusinessTraveller.StatefulData<{}>['status'] | undefined => {
    if (!isItemPoliciesEnabled) {
      return businessCarHireBudgets[LEGACY_CAR_HIRE_BUDGET_KEY]?.status
    }

    const cacheKey = getCarHireItemPolicyBudgetKey({ carHireItems, currencyCode, employeeIds })
    if (!cacheKey) {
      return undefined
    }

    return itemPolicyEvaluations[cacheKey]?.status ?? 'pending'
  },
)

/**
 * Selector to get total car hire budget (daily budget × number of days).
 * Used for approval flow when scope is OUTOFPOLICYONLY.
 *
 * Note: Components should use useBusinessTravellerBusinessCarHireBudgetFetcher
 * to trigger fetching. This selector only reads from the store.
 */
export const selectBusinessTravellerCarHireBudgetTotal = createSelector(
  selectIsLebtItemPoliciesEnabled,
  (state: App.State) => state.businessTraveller.businessCarHireBudgets,
  (state: App.State) => state.businessTraveller.itemPolicyEvaluations ?? {},
  getCarHireItems,
  (state: App.State) => state.geo.currentCurrency,
  selectSelectedTravellerEmployeeIds,
  (
    isItemPoliciesEnabled,
    businessCarHireBudgets,
    itemPolicyEvaluations,
    carHireItems,
    currencyCode,
    employeeIds,
  ): number => {
    if (isItemPoliciesEnabled) {
      const cacheKey = getCarHireItemPolicyBudgetKey({ carHireItems, currencyCode, employeeIds })
      const itemPolicyBudget = cacheKey ? itemPolicyEvaluations[cacheKey] : undefined

      if (
        itemPolicyBudget?.status !== 'success' ||
        itemPolicyBudget.itemPolicyType !== 'CAR_HIRE' ||
        carHireItems.length === 0
      ) {
        return 0
      }

      return itemPolicyBudget.summary.minTotalAmount
    }

    const budgetData = businessCarHireBudgets[LEGACY_CAR_HIRE_BUDGET_KEY]

    if (
      budgetData?.status !== 'success' ||
      !budgetData.carHireBudgets?.length ||
      carHireItems.length === 0
    ) {
      return 0
    }

    const dailyBudget = budgetData.carHireBudgets[0].budget
    const numberOfDays = carHireItems[0].offer.duration
    return dailyBudget * numberOfDays
  },
)

export const selectCarHireBudgetInfo = createSelector(
  selectBusinessTravellerCarHireBudgetTotal,
  (state: App.State) => getCarHireTotals(state),
  selectBusinessTravellerCurrentApprovalRequest,
  selectBusinessTravellerApprovalPolicyForUser,
  (
    carHireBudgetTotal,
    carHireTotals,
    currentApprovalRequest,
    approvalPolicy,
  ): CarHireBudgetInfo => {
    const carHireTotal = carHireTotals.hasRequiredData ? carHireTotals.data.price : 0
    const approvedBudget = currentApprovalRequest?.status === 'APPROVED'
      ? (currentApprovalRequest.total ?? 0)
      : 0

    if (approvedBudget > 0) {
      const priceBufferPercent = approvalPolicy?.priceBuffer ?? 0
      const maxAllowed = approvedBudget + (approvedBudget * priceBufferPercent) / 100
      return {
        approvedBudget,
        carHireBudgetTotal,
        carHireTotal,
        hasMissingBudget: false,
        isOutOfBudget: carHireTotal > maxAllowed,
        showBudgetBreakdown: true,
      }
    }

    return {
      approvedBudget,
      carHireBudgetTotal,
      carHireTotal,
      hasMissingBudget: carHireBudgetTotal <= 0,
      isOutOfBudget: carHireBudgetTotal > 0 && carHireTotal > carHireBudgetTotal,
      showBudgetBreakdown: carHireBudgetTotal > 0,
    }
  },
)
